import { useEffect, useRef, useState, useMemo } from "react";
import { AppToolbarButton } from "./WhatsNew";
import Map from "ol/Map";
import View from "ol/View";
import { fromLonLat } from "ol/proj";
import { Feature } from "ol";
import { LineString } from "ol/geom";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import { Stroke, Style } from "ol/style";
import "ol/ol.css";
import { useProject } from "./ProjectContext";
import { projectCoordsToLonLat, createBaseLayers } from "./mapUtils";

interface RawPoint {
  station: number;
  easting: number;
  northing: number;
  elevation: number;
}

const PANEL_BG = "linear-gradient(180deg, rgba(0,0,0,0.82), rgba(0,0,0,0.72))";
const PANEL_BORDER = "rgba(255,255,255,0.12)";

/** Parse the LandXML to get raw UTM points (reuses logic from parseLandXML but returns raw coords) */
async function fetchRawCenterline(endpoint: string): Promise<RawPoint[]> {
  const resp = await fetch(endpoint);
  const xml = await resp.text();
  const { parseLandXML } = await import("./parseLandXML");
  const { rawPoints } = await parseLandXML(xml);
  return rawPoints;
}

/* ── Elevation profile canvas drawing ── */
function ElevationProfile({ points, width, height }: { points: RawPoint[]; width: number; height: number }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || points.length < 2) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    ctx.scale(dpr, dpr);

    const pad = { top: 24, right: 16, bottom: 32, left: 52 };
    const w = width - pad.left - pad.right;
    const h = height - pad.top - pad.bottom;

    const stations = points.map((p) => p.station);
    const elevations = points.map((p) => p.elevation);
    const minS = Math.min(...stations);
    const maxS = Math.max(...stations);
    const minE = Math.min(...elevations);
    const maxE = Math.max(...elevations);
    const eRange = maxE - minE || 1;
    const ePad = eRange * 0.1;

    const sx = (s: number) => pad.left + ((s - minS) / (maxS - minS)) * w;
    const sy = (e: number) => pad.top + h - ((e - (minE - ePad)) / (eRange + 2 * ePad)) * h;

    // Background
    ctx.fillStyle = "rgba(10, 22, 40, 0.95)";
    ctx.fillRect(0, 0, width, height);

    // Grid lines
    ctx.strokeStyle = "rgba(255,255,255,0.06)";
    ctx.lineWidth = 1;
    const nGridY = 5;
    for (let i = 0; i <= nGridY; i++) {
      const e = (minE - ePad) + (eRange + 2 * ePad) * (i / nGridY);
      const y = sy(e);
      ctx.beginPath();
      ctx.moveTo(pad.left, y);
      ctx.lineTo(pad.left + w, y);
      ctx.stroke();

      // Label
      ctx.fillStyle = "rgba(255,255,255,0.4)";
      ctx.font = "10px system-ui, sans-serif";
      ctx.textAlign = "right";
      ctx.fillText(`${e.toFixed(0)} m`, pad.left - 6, y + 3);
    }

    // Station labels
    const nGridX = 5;
    ctx.textAlign = "center";
    for (let i = 0; i <= nGridX; i++) {
      const s = minS + (maxS - minS) * (i / nGridX);
      const x = sx(s);
      ctx.beginPath();
      ctx.moveTo(x, pad.top);
      ctx.lineTo(x, pad.top + h);
      ctx.stroke();

      ctx.fillStyle = "rgba(255,255,255,0.4)";
      ctx.fillText(`${s.toFixed(0)}`, x, pad.top + h + 14);
    }

    // Axis labels
    ctx.fillStyle = "rgba(255,255,255,0.3)";
    ctx.font = "9px system-ui, sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Station (m)", pad.left + w / 2, height - 4);

    ctx.save();
    ctx.translate(12, pad.top + h / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.fillText("Elevation (m)", 0, 0);
    ctx.restore();

    // Profile line
    ctx.beginPath();
    ctx.moveTo(sx(stations[0]), sy(elevations[0]));
    for (let i = 1; i < points.length; i++) {
      ctx.lineTo(sx(stations[i]), sy(elevations[i]));
    }
    ctx.strokeStyle = "#4fc3f7";
    ctx.lineWidth = 2;
    ctx.stroke();

    // Fill under
    ctx.lineTo(sx(stations[stations.length - 1]), sy(minE - ePad));
    ctx.lineTo(sx(stations[0]), sy(minE - ePad));
    ctx.closePath();
    ctx.fillStyle = "rgba(79, 195, 247, 0.08)";
    ctx.fill();

    // Title
    ctx.fillStyle = "rgba(255,255,255,0.6)";
    ctx.font = "bold 11px system-ui, sans-serif";
    ctx.textAlign = "left";
    ctx.fillText("Elevation Profile", pad.left, 14);

    // Stats
    ctx.font = "10px system-ui, sans-serif";
    ctx.fillStyle = "rgba(255,255,255,0.35)";
    ctx.textAlign = "right";
    ctx.fillText(
      `Length: ${(maxS - minS).toFixed(0)} m  |  Elev: ${minE.toFixed(1)} – ${maxE.toFixed(1)} m`,
      pad.left + w,
      14,
    );
  }, [points, width, height]);

  return (
    <canvas
      ref={canvasRef}
      style={{ width, height, borderRadius: 8, display: "block" }}
    />
  );
}

export function TunnelLineView({ onBack }: { onBack: () => void }) {
  const project = useProject();
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<Map | null>(null);
  const [rawPoints, setRawPoints] = useState<RawPoint[]>([]);
  const [mapSize, setMapSize] = useState({ width: 800, height: 600 });

  // Fetch centerline
  useEffect(() => {
    fetchRawCenterline(project.centerlineEndpoint).then(setRawPoints);
  }, [project.centerlineEndpoint]);

  // Track container size
  useEffect(() => {
    const el = mapRef.current?.parentElement;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setMapSize({ width: entry.contentRect.width, height: entry.contentRect.height });
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Initialize map
  useEffect(() => {
    if (!mapRef.current || mapInstance.current) return;

    const map = new Map({
      target: mapRef.current,
      layers: createBaseLayers(),
      view: new View({
        center: fromLonLat([7.8, 60.9]),
        zoom: 13,
      }),
      controls: [],
    });
    mapInstance.current = map;

    return () => {
      map.setTarget(undefined);
      mapInstance.current = null;
    };
  }, []);

  // Draw centerline on map and fit view
  useEffect(() => {
    const map = mapInstance.current;
    if (!map || rawPoints.length < 2) return;

    const coords = rawPoints.map((p) => {
      const [lon, lat] = projectCoordsToLonLat(p.easting, p.northing);
      return fromLonLat([lon, lat]);
    });

    const lineFeature = new Feature({ geometry: new LineString(coords) });
    const vectorSource = new VectorSource({ features: [lineFeature] });
    const vectorLayer = new VectorLayer({
      source: vectorSource,
      style: new Style({
        stroke: new Stroke({ color: "#4fc3f7", width: 3 }),
      }),
    });

    map.addLayer(vectorLayer);

    // Fit to centerline extent with padding
    const extent = vectorSource.getExtent();
    if (extent && isFinite(extent[0])) {
      map.getView().fit(extent, { padding: [60, 60, 60, 60], maxZoom: 16 });
    }

    return () => {
      map.removeLayer(vectorLayer);
    };
  }, [rawPoints]);

  const profileHeight = 180;

  return (
    <div style={{
      height: "100dvh",
      display: "flex",
      flexDirection: "column",
      background: "#0a1628",
      color: "#f7f8fb",
      fontFamily: "system-ui, -apple-system, sans-serif",
    }}>
      {/* Header */}
      <header style={{
        flexShrink: 0,
        padding: "12px 20px",
        borderBottom: "1px solid rgba(255,255,255,0.06)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button
            type="button"
            onClick={onBack}
            style={{
              background: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.12)",
              borderRadius: 8,
              color: "#f7f8fb",
              cursor: "pointer",
              padding: "6px 12px",
              fontSize: "0.8rem",
              fontWeight: 600,
            }}
          >
            ← Back
          </button>
          <div>
            <div style={{ fontSize: "1rem", fontWeight: 700 }}>Tunnel Line</div>
            <div style={{ fontSize: "0.7rem", opacity: 0.45 }}>{project.name} — Centerline alignment</div>
          </div>
        </div>
        <AppToolbarButton />
      </header>

      {/* Map */}
      <div style={{ flex: 1, minHeight: 0, position: "relative" }}>
        <div ref={mapRef} style={{ width: "100%", height: "100%" }} />

        {/* Info overlay */}
        {rawPoints.length > 0 && (
          <div style={{
            position: "absolute",
            top: 12,
            right: 12,
            padding: "10px 14px",
            borderRadius: 12,
            background: PANEL_BG,
            border: `1px solid ${PANEL_BORDER}`,
            backdropFilter: "blur(14px)",
            WebkitBackdropFilter: "blur(14px)",
            fontSize: "0.75rem",
            display: "flex",
            flexDirection: "column",
            gap: 4,
          }}>
            <div style={{ fontWeight: 700, fontSize: "0.7rem", opacity: 0.5, textTransform: "uppercase", letterSpacing: "0.04em" }}>
              Alignment info
            </div>
            <div>
              <span style={{ opacity: 0.5 }}>Length: </span>
              <span style={{ fontWeight: 600 }}>{rawPoints[rawPoints.length - 1].station.toFixed(0)} m</span>
            </div>
            <div>
              <span style={{ opacity: 0.5 }}>Elevation: </span>
              <span style={{ fontWeight: 600 }}>
                {Math.min(...rawPoints.map((p) => p.elevation)).toFixed(1)} – {Math.max(...rawPoints.map((p) => p.elevation)).toFixed(1)} m
              </span>
            </div>
            <div>
              <span style={{ opacity: 0.5 }}>Coordinate system: </span>
              <span style={{ fontWeight: 600 }}>UTM 32N (EPSG:25832)</span>
            </div>
          </div>
        )}
      </div>

      {/* Elevation profile */}
      <div style={{
        flexShrink: 0,
        height: profileHeight,
        borderTop: "1px solid rgba(255,255,255,0.06)",
      }}>
        {rawPoints.length > 0 && (
          <ElevationProfile
            points={rawPoints}
            width={mapSize.width || 800}
            height={profileHeight}
          />
        )}
      </div>
    </div>
  );
}
